import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { getUserTypeLabel } from '../../../constants';
import { adminService } from '../../../services/adminService';
import { programService } from '../../../services/programService';
import { observationService } from '../../../services/observationService';
import { useAuthStore } from '../../../store/authStore';
import Chips from '../../atoms/Chips';
import AddProgramModal from '../../organisms/AddProgramModal';
import { EditProgramModal } from '../../organisms/EditProgramModal';
import { AddObservationBehaviorModal } from '../../organisms/AddObservationBehaviorModal';
import { EditObservationBehaviorModal } from '../../organisms/EditObservationBehaviorModal';
import QnABlock from '../../organisms/QnABlock';
import SessionChart from '../../organisms/SessionChart';
import SessionDetailModal from '../../organisms/SessionDetailModal';
import StatisticsBlock from '../../organisms/StatisticsBlock';
import ReactMarkdown from 'react-markdown';

const Container = styled.div`
  max-width: 1200px;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  color: #333;
  margin: 0 0 24px;
`;

const Card = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
`;

const CardTitle = styled.h2`
  font-size: 20px;
  color: #333;
  margin: 0 0 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #f0f0f0;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  margin-top: 16px;
`;

const InfoItem = styled.div`
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border-left: 4px solid #007bff;
`;

const InfoLabel = styled.div`
  font-size: 13px;
  color: #666;
  margin-bottom: 6px;
  font-weight: 500;
`;

const InfoValue = styled.div`
  font-size: 16px;
  color: #333;
  font-weight: 600;
`;

const WelcomeMessage = styled.p`
  font-size: 16px;
  color: #666;
  line-height: 1.6;
`;

const Select = styled.select`
  width: 100%;
  max-width: 400px;
  padding: 12px 16px;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  font-size: 15px;
  color: #333;
  background-color: white;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 24px;

  &:hover {
    border-color: #007bff;
  }

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }
`;

const Badge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${props => {
    switch (props.$type) {
      case 'admin': return '#e7f3ff';
      case 'active': return '#e7f3ff';
      case 'completed': return '#d4edda';
      case 'paused': return '#fff3cd';
      case 'inactive': return '#f0f0f0';
      case '진행중': return '#e7f3ff';
      case '완료': return '#d4edda';
      case '중지': return '#fff3cd';
      default: return '#f0f0f0';
    }
  }};
  color: ${props => {
    switch (props.$type) {
      case 'admin': return '#007bff';
      case 'active': return '#007bff';
      case 'completed': return '#28a745';
      case 'paused': return '#ffc107';
      case 'inactive': return '#666';
      case '진행중': return '#007bff';
      case '완료': return '#28a745';
      case '중지': return '#ffc107';
      default: return '#666';
    }
  }};
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #999;
  font-size: 16px;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 16px;
`;

const SessionCard = styled.div`
  padding: 16px;
  margin-bottom: 12px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #28a745;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #e9ecef;
    transform: translateX(4px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #f0f0f0;
`;

const AddButton = styled.button`
  padding: 8px 16px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #0056b3;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const EditButton = styled.button`
  padding: 6px 12px;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #218838;
  }
`;

const DeleteButton = styled.button`
  padding: 6px 12px;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #c82333;
  }
`;

const StatusSelect = styled.select`
  padding: 6px 12px;
  border: 2px solid #dee2e6;
  border-radius: 6px;
  font-size: 14px;
  color: #333;
  background-color: white;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 600;

  &:hover {
    border-color: #007bff;
  }

  &:focus {
    outline: none;
    border-color: #007bff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const InfoPage = () => {
  const { userProfile } = useAuthStore();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudentId, setSelectedStudentId] = useState('');

  // Program related state
  const [programs, setPrograms] = useState([]);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [selectedMajorCategory, setSelectedMajorCategory] = useState('');
  const [selectedMiddleCategory, setSelectedMiddleCategory] = useState('');
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [allSessions, setAllSessions] = useState([]); // 전체 통계용
  const [allPrograms, setAllPrograms] = useState([]); // 전체 프로그램 목록 (자동완성용)

  // Modal state
  const [isSessionModalOpen, setIsSessionModalOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [isAddProgramModalOpen, setIsAddProgramModalOpen] = useState(false);
  const [isEditProgramModalOpen, setIsEditProgramModalOpen] = useState(false);
  const [programToEdit, setProgramToEdit] = useState(null);

  // Observation related state
  const [observations, setObservations] = useState([]);
  const [loadingObservations, setLoadingObservations] = useState(false);
  const [isAddObservationModalOpen, setIsAddObservationModalOpen] = useState(false);
  const [isEditObservationModalOpen, setIsEditObservationModalOpen] = useState(false);
  const [observationToEdit, setObservationToEdit] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchAllPrograms();
  }, []);

  // Auto-select student for mom users
  useEffect(() => {
    if (userProfile && userProfile.type === 'mom' && users.length > 0) {
      setSelectedStudentId(userProfile.id);
    }
  }, [userProfile, users]);

  // Load programs when student is selected
  useEffect(() => {
    if (selectedStudentId) {
      fetchPrograms(selectedStudentId);
      fetchObservations(selectedStudentId);
    } else {
      setPrograms([]);
      setSelectedMajorCategory('');
      setSelectedMiddleCategory('');
      setSelectedProgram(null);
      setSessions([]);
      setObservations([]);
    }
  }, [selectedStudentId]);

  // Auto-select first major category when programs are loaded
  useEffect(() => {
    if (programs.length > 0) {
      const majorCategories = getMajorCategories();
      if (majorCategories.length > 0 && !selectedMajorCategory) {
        setSelectedMajorCategory(majorCategories[0]);
      }
    }
  }, [programs]);

  // Reset middle category when major category changes and auto-select first
  useEffect(() => {
    if (selectedMajorCategory) {
      const middleCategories = getMiddleCategories();
      if (middleCategories.length > 0) {
        setSelectedMiddleCategory(middleCategories[0]);
      } else {
        setSelectedMiddleCategory('');
      }
    } else {
      setSelectedMiddleCategory('');
    }
    setSelectedProgram(null);
    setSessions([]);
  }, [selectedMajorCategory]);

  // Reset program when middle category changes and auto-select first
  useEffect(() => {
    if (selectedMiddleCategory) {
      const programsList = getProgramsForCategory();
      if (programsList.length > 0) {
        setSelectedProgram(programsList[0]);
      } else {
        setSelectedProgram(null);
      }
    } else {
      setSelectedProgram(null);
    }
    setSessions([]);
  }, [selectedMiddleCategory]);

  // Load sessions when program is selected
  useEffect(() => {
    if (selectedProgram && selectedStudentId) {
      fetchSessions(selectedStudentId, selectedProgram.program.id);
    } else {
      setSessions([]);
    }
  }, [selectedProgram, selectedStudentId]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await adminService.getAllUsers();
      setUsers(data);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllPrograms = async () => {
    try {
      const data = await programService.getAllPrograms();
      // 프로그램 데이터를 programs와 동일한 형식으로 변환
      const formattedData = data.map(program => ({
        program: program
      }));
      setAllPrograms(formattedData);
    } catch (err) {
      console.error('Failed to fetch all programs:', err);
    }
  };

  const getMomUsers = () => {
    const allMoms = users.filter(u => u.type === 'mom');

    if (!userProfile) return [];

    // Admin: 모든 학생 볼 수 있음
    if (userProfile.type === 'admin') {
      return allMoms;
    }

    // Teacher: 자신에게 배정된 학생만 볼 수 있음
    if (userProfile.type === 'teacher') {
      return allMoms.filter(mom => mom.teacher_ids && mom.teacher_ids.includes(userProfile.id));
    }

    // Mom: 본인의 정보만 볼 수 있음
    if (userProfile.type === 'mom') {
      return allMoms.filter(mom => mom.id === userProfile.id);
    }

    return [];
  };

  const getSelectedStudent = () => {
    return users.find(u => u.id === selectedStudentId);
  };

  const getTeacherNames = (teacherIds) => {
    if (!teacherIds || teacherIds.length === 0) return '-';
    const names = teacherIds
      .map(teacherId => {
        const teacher = users.find(u => u.id === teacherId);
        return teacher ? teacher.name : null;
      })
      .filter(Boolean);
    return names.length > 0 ? names.join(', ') : '-';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('ko-KR');
    } catch {
      return '-';
    }
  };

  const fetchPrograms = async (studentId) => {
    try {
      setLoadingPrograms(true);
      const programsData = await programService.getStudentPrograms(studentId);
      setPrograms(programsData);

      // 전체 세션 데이터 가져오기 (모든 프로그램의 세션)
      const allSessionsData = [];
      for (const programData of programsData) {
        const sessionsData = await programService.getProgramSessions(studentId, programData.program.id);
        allSessionsData.push(...sessionsData);
      }
      setAllSessions(allSessionsData);
    } catch (error) {
      console.error('Failed to fetch programs:', error);
    } finally {
      setLoadingPrograms(false);
    }
  };

  const fetchSessions = async (studentId, programId) => {
    try {
      const sessionsData = await programService.getProgramSessions(studentId, programId);
      setSessions(sessionsData);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    }
  };

  const getMajorCategories = () => {
    return programService.getMajorCategories(programs);
  };

  const getMiddleCategories = () => {
    if (!selectedMajorCategory) return [];
    return programService.getMiddleCategories(programs, selectedMajorCategory);
  };

  const getProgramsForCategory = () => {
    if (!selectedMajorCategory || !selectedMiddleCategory) return [];
    return programService.getPrograms(programs, selectedMajorCategory, selectedMiddleCategory);
  };

  const handleSessionClick = (session) => {
    setSelectedSession(session);
    setIsSessionModalOpen(true);
  };

  const handleCloseSessionModal = () => {
    setIsSessionModalOpen(false);
    setSelectedSession(null);
  };

  const handleAddProgram = async (studentId, programData) => {
    await programService.addProgramToStudent(studentId, programData);
    // 프로그램 목록 새로고침
    await fetchPrograms(studentId);
  };

  const handleEditProgram = async (programId, programData) => {
    await programService.updateProgram(programId, programData);
    // 프로그램 목록 새로고침
    await fetchPrograms(selectedStudentId);
  };

  const handleDeleteProgram = async (programId, assignmentId) => {
    if (window.confirm('정말 이 프로그램을 삭제하시겠습니까?\n연결된 모든 세션과 기록도 함께 삭제됩니다.')) {
      try {
        await programService.deleteProgram(programId, assignmentId);
        // 프로그램 목록 새로고침
        await fetchPrograms(selectedStudentId);
        // 선택된 프로그램이 삭제된 경우 초기화
        if (selectedProgram && selectedProgram.id === programId) {
          setSelectedProgram(null);
          setSessions([]);
        }
        alert('프로그램이 성공적으로 삭제되었습니다.');
      } catch (error) {
        alert('프로그램 삭제에 실패했습니다: ' + error.message);
      }
    }
  };

  const handleOpenEditModal = (program) => {
    setProgramToEdit(program);
    setIsEditProgramModalOpen(true);
  };

  const handleStatusChange = async (programId, assignmentId, newStatus) => {
    try {
      // 상태 변경 확인
      const statusLabels = {
        'active': '진행중',
        'completed': '완료',
        'paused': '중지'
      };

      const confirmed = window.confirm(
        `프로그램 상태를 "${statusLabels[newStatus]}"로 변경하시겠습니까?`
      );

      if (!confirmed) {
        return;
      }

      await programService.updateProgramStatus(programId, assignmentId, newStatus);

      // 프로그램 목록 새로고침
      await fetchPrograms(selectedStudentId);

      alert('프로그램 상태가 변경되었습니다.');
    } catch (error) {
      console.error('Failed to update program status:', error);
      alert('프로그램 상태 변경에 실패했습니다.');
    }
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      'active': '진행중',
      'completed': '완료',
      'paused': '중지',
      '진행중': '진행중',
      '완료': '완료',
      '중지': '중지'
    };
    return statusMap[status] || status;
  };

  // Observation related functions
  const fetchObservations = async (studentId) => {
    try {
      setLoadingObservations(true);
      const summaries = await observationService.getObservationSummary(studentId);
      setObservations(summaries);
    } catch (error) {
      console.error('Failed to fetch observations:', error);
    } finally {
      setLoadingObservations(false);
    }
  };

  const handleAddObservation = async (studentId, observationData) => {
    await observationService.addObservationBehavior(studentId, observationData);
    await fetchObservations(studentId);
  };

  const handleEditObservation = async (behaviorId, observationData) => {
    await observationService.updateObservationBehavior(behaviorId, observationData);
    await fetchObservations(selectedStudentId);
  };

  const handleDeleteObservation = async (behaviorId) => {
    if (window.confirm('정말 이 관찰 항목을 삭제하시겠습니까?\n모든 관찰 기록도 함께 삭제됩니다.')) {
      try {
        await observationService.deleteObservationBehavior(behaviorId);
        await fetchObservations(selectedStudentId);
        alert('관찰 항목이 성공적으로 삭제되었습니다.');
      } catch (error) {
        alert('관찰 항목 삭제에 실패했습니다: ' + error.message);
      }
    }
  };

  const handleOpenEditObservationModal = (observation) => {
    setObservationToEdit({
      id: observation.behaviorId,
      name: observation.behaviorName,
      description: observation.behaviorDescription
    });
    setIsEditObservationModalOpen(true);
  };

  return (
    <Container>
      <PageTitle>정보</PageTitle>

      <Card>
        <CardTitle>환영합니다! 👋</CardTitle>
        <WelcomeMessage>
          {userProfile?.name}님, ABA Web에 오신 것을 환영합니다.
          <br />
          현재 {getUserTypeLabel(userProfile?.type)} 계정으로 로그인되어 있습니다.
        </WelcomeMessage>
      </Card>

      <Card>
        <CardTitle>학생 정보</CardTitle>

        {loading && <LoadingMessage>정보를 불러오는 중...</LoadingMessage>}

        {!loading && (
          <>
            {/* Show dropdown for admin and teacher, hide for mom */}
            {userProfile && (userProfile.type === 'admin' || userProfile.type === 'teacher') && (
              <Select
                value={selectedStudentId}
                onChange={(e) => setSelectedStudentId(e.target.value)}
              >
                <option value="">학부모(학생)를 선택하세요</option>
                {getMomUsers().map((mom) => (
                  <option key={mom.id} value={mom.id}>
                    {mom.name} - {mom.studentName || '학생명 없음'}
                  </option>
                ))}
              </Select>
            )}

            {selectedStudentId && getSelectedStudent() && (
              <InfoGrid>
                <InfoItem>
                  <InfoLabel>학부모 이름</InfoLabel>
                  <InfoValue>{getSelectedStudent().name}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>학생 이름</InfoLabel>
                  <InfoValue>{getSelectedStudent().studentName || '-'}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>전화번호</InfoLabel>
                  <InfoValue>{getSelectedStudent().phone}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>이메일</InfoLabel>
                  <InfoValue>{getSelectedStudent().email}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>출생년도</InfoLabel>
                  <InfoValue>{getSelectedStudent().birthYear || '-'}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>성별</InfoLabel>
                  <InfoValue>
                    {getSelectedStudent().gender === 'male' ? '남아' :
                     getSelectedStudent().gender === 'female' ? '여아' : '-'}
                  </InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>활성 상태</InfoLabel>
                  <InfoValue>
                    <Badge $type={getSelectedStudent().isActive ? 'active' : 'inactive'}>
                      {getSelectedStudent().isActive ? '활성' : '비활성'}
                    </Badge>
                  </InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>가입일</InfoLabel>
                  <InfoValue>{formatDate(getSelectedStudent().createdAt)}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>담당 치료사</InfoLabel>
                  <InfoValue>{getTeacherNames(getSelectedStudent().teacher_ids)}</InfoValue>
                </InfoItem>
              </InfoGrid>
            )}

            {!selectedStudentId && getMomUsers().length === 0 && (
              <EmptyMessage>
                {userProfile?.type === 'teacher'
                  ? '배정된 학생이 없습니다.'
                  : '학부모(학생)을 선택해주세요.'}
              </EmptyMessage>
            )}

            {!selectedStudentId && getMomUsers().length > 0 && userProfile?.type !== 'mom' && (
              <EmptyMessage>학부모(학생)을 선택해주세요.</EmptyMessage>
            )}
          </>
        )}
      </Card>

      {/* 통계 블록 */}
      {selectedStudentId && programs.length > 0 && (
        <Card>
          <CardTitle>전체 통계</CardTitle>
          <StatisticsBlock programs={programs} sessions={allSessions} />
        </Card>
      )}

      {/* 프로그램 정보 섹션 */}
      {selectedStudentId && (
        <Card>
          <CardHeader>
            <CardTitle style={{ margin: 0, paddingBottom: 0, border: 'none' }}>수행 중인 프로그램</CardTitle>
            {(userProfile?.type === 'admin' || userProfile?.type === 'teacher') && (
              <AddButton onClick={() => setIsAddProgramModalOpen(true)}>
                + 프로그램 추가
              </AddButton>
            )}
          </CardHeader>

          {loadingPrograms && <LoadingMessage>프로그램 정보를 불러오는 중...</LoadingMessage>}

          {!loadingPrograms && programs.length === 0 && (
            <EmptyMessage>배정된 프로그램이 없습니다.</EmptyMessage>
          )}

          {!loadingPrograms && programs.length > 0 && (
            <>
              {/* 대주제 선택 */}
              <Chips
                label="대주제"
                options={getMajorCategories()}
                value={selectedMajorCategory}
                onChange={setSelectedMajorCategory}
              />

              {/* 중주제 선택 */}
              {selectedMajorCategory && getMiddleCategories().length > 0 && (
                <Chips
                  label="중주제"
                  options={getMiddleCategories()}
                  value={selectedMiddleCategory}
                  onChange={setSelectedMiddleCategory}
                />
              )}

              {/* 프로그램 선택 */}
              {selectedMajorCategory && selectedMiddleCategory && getProgramsForCategory().length > 0 && (
                <Chips
                  label="프로그램"
                  options={getProgramsForCategory().map(p => p.program.minorCategory)}
                  value={selectedProgram?.program.minorCategory || ''}
                  onChange={(minorCategory) => {
                    const program = getProgramsForCategory().find(
                      p => p.program.minorCategory === minorCategory
                    );
                    setSelectedProgram(program);
                  }}
                />
              )}

              {/* 선택된 프로그램 상세 정보 */}
              {selectedProgram && (
                <>
                  {/* 수정/삭제 버튼 */}
                  {(userProfile?.type === 'admin' || userProfile?.type === 'teacher') && (
                    <ButtonGroup style={{ marginTop: '24px', marginBottom: '12px' }}>
                      <EditButton onClick={() => handleOpenEditModal(selectedProgram.program)}>
                        수정
                      </EditButton>
                      <DeleteButton
                        onClick={() => handleDeleteProgram(selectedProgram.program.id, selectedProgram.id)}
                      >
                        삭제
                      </DeleteButton>
                    </ButtonGroup>
                  )}
                  <InfoGrid style={{ marginTop: '12px' }}>
                    <InfoItem>
                      <InfoLabel>소주제</InfoLabel>
                      <InfoValue>{selectedProgram.program.minorCategory}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>세부 목표</InfoLabel>
                      <InfoValue>{selectedProgram.program.detailedGoal}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>지시 방법</InfoLabel>
                      <InfoValue>{selectedProgram.program.instructionMethod}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>반복 횟수</InfoLabel>
                      <InfoValue>{selectedProgram.program.repetitionCount}회</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>도달 성공 기준</InfoLabel>
                      <InfoValue>{selectedProgram.program.successCriteria}회</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>행동 유형</InfoLabel>
                      <InfoValue>
                        {selectedProgram.program.behaviorType === 'positive_above' ? '긍정적 행동 증가' :
                         selectedProgram.program.behaviorType === 'abnormal_below' ? '문제행동 감소' : '-'}
                      </InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>프로그램 성공 기준</InfoLabel>
                      <InfoValue>
                        {selectedProgram.program.programSuccessCriteria}회{' '}
                        {selectedProgram.program.programSuccessMode === 'consecutive' ? '연속' :
                         selectedProgram.program.programSuccessMode === 'cumulative' ? '누적' : ''}
                      </InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>지시자</InfoLabel>
                      <InfoValue>{selectedProgram.program.instructor}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>진행 상태</InfoLabel>
                      <InfoValue>
                        {(userProfile?.type === 'admin' || userProfile?.type === 'teacher') ? (
                          <StatusSelect
                            value={selectedProgram.assignment.status || selectedProgram.program.status || 'active'}
                            onChange={(e) => handleStatusChange(
                              selectedProgram.program.id,
                              selectedProgram.assignmentId,
                              e.target.value
                            )}
                          >
                            <option value="active">진행중</option>
                            <option value="completed">완료</option>
                            <option value="paused">중지</option>
                          </StatusSelect>
                        ) : (
                          <Badge $type={getStatusLabel(selectedProgram.assignment.status || selectedProgram.program.status)}>
                            {getStatusLabel(selectedProgram.assignment.status || selectedProgram.program.status)}
                          </Badge>
                        )}
                      </InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>촉구 방법</InfoLabel>
                      <InfoValue>{selectedProgram.program.promptingMethod}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>강화 스케줄</InfoLabel>
                      <InfoValue>{selectedProgram.program.reinforcementSchedule}</InfoValue>
                    </InfoItem>
                  </InfoGrid>

                  {/* 세션별 성공률 그래프 */}
                  {sessions.length > 0 && (
                    <SessionChart
                      sessions={sessions}
                      successCriteria={selectedProgram.program.successCriteria}
                      repetitionCount={selectedProgram.program.repetitionCount}
                    />
                  )}

                  {/* 세션 기록 */}
                  {sessions.length > 0 && (
                    <div style={{ marginTop: '32px' }}>
                      <h3 style={{ fontSize: '18px', marginBottom: '16px', color: '#333' }}>
                        최근 수행 기록 ({sessions.length}개)
                      </h3>
                      {sessions.map((session) => (
                        <SessionCard
                          key={session.id}
                          onClick={() => handleSessionClick(session)}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <strong>{formatDate(session.sessionDate)}</strong>
                            <span style={{ color: '#666' }}>
                              성공률: {((session.successCount / session.repetitionCount) * 100).toFixed(0)}%
                            </span>
                          </div>
                          <div style={{ color: '#666', fontSize: '14px' }}>
                            성공: {session.successCount}회 / 실패: {session.failureCount}회
                            (총 {session.repetitionCount}회)
                          </div>
                          {session.notes && (
                            <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
                              메모: {session.notes}
                            </div>
                          )}
                          <div style={{ marginTop: '8px', fontSize: '13px', color: '#007bff', fontWeight: '500' }}>
                            클릭하여 상세 기록 보기 →
                          </div>
                        </SessionCard>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </Card>
      )}

      {/* QnA 블록 */}
      {selectedStudentId && (
        <Card>
          <CardTitle>질문과 답변 (Q&A)</CardTitle>
          <QnABlock studentId={selectedStudentId} programs={programs} />
        </Card>
      )}

      {/* 관찰 항목 섹션 */}
      {selectedStudentId && (
        <Card>
          <CardHeader>
            <CardTitle style={{ margin: 0, paddingBottom: 0, border: 'none' }}>관찰 항목</CardTitle>
            {(userProfile?.type === 'admin' || userProfile?.type === 'teacher') && (
              <AddButton onClick={() => setIsAddObservationModalOpen(true)}>
                + 관찰 항목 추가
              </AddButton>
            )}
          </CardHeader>

          {loadingObservations && <LoadingMessage>관찰 항목을 불러오는 중...</LoadingMessage>}

          {!loadingObservations && observations.length === 0 && (
            <EmptyMessage>등록된 관찰 항목이 없습니다.</EmptyMessage>
          )}

          {!loadingObservations && observations.length > 0 && (
            <div>
              {observations.map((observation) => (
                <div
                  key={observation.behaviorId}
                  style={{
                    padding: '20px',
                    marginBottom: '16px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    borderLeft: '4px solid #dc3545'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <h3 style={{ fontSize: '18px', color: '#333', margin: 0 }}>
                      {observation.behaviorName}
                    </h3>
                    {(userProfile?.type === 'admin' || userProfile?.type === 'teacher') && (
                      <ButtonGroup>
                        <EditButton onClick={() => handleOpenEditObservationModal(observation)}>
                          수정
                        </EditButton>
                        <DeleteButton onClick={() => handleDeleteObservation(observation.behaviorId)}>
                          삭제
                        </DeleteButton>
                      </ButtonGroup>
                    )}
                  </div>

                  <div style={{ marginBottom: '16px', color: '#666', fontSize: '14px' }}>
                    <ReactMarkdown>{observation.behaviorDescription}</ReactMarkdown>
                  </div>

                  <InfoGrid>
                    <InfoItem style={{ borderLeft: '4px solid #dc3545' }}>
                      <InfoLabel>오늘</InfoLabel>
                      <InfoValue style={{ color: '#dc3545' }}>{observation.todayCount}회</InfoValue>
                    </InfoItem>
                    <InfoItem style={{ borderLeft: '4px solid #ffc107' }}>
                      <InfoLabel>최근 7일</InfoLabel>
                      <InfoValue style={{ color: '#ffc107' }}>{observation.weekCount}회</InfoValue>
                    </InfoItem>
                    <InfoItem style={{ borderLeft: '4px solid #28a745' }}>
                      <InfoLabel>최근 30일</InfoLabel>
                      <InfoValue style={{ color: '#28a745' }}>{observation.monthCount}회</InfoValue>
                    </InfoItem>
                  </InfoGrid>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* 세션 상세 모달 */}
      <SessionDetailModal
        isOpen={isSessionModalOpen}
        onClose={handleCloseSessionModal}
        session={selectedSession}
      />

      {/* 프로그램 추가 모달 */}
      <AddProgramModal
        isOpen={isAddProgramModalOpen}
        onClose={() => setIsAddProgramModalOpen(false)}
        onSubmit={handleAddProgram}
        studentId={selectedStudentId}
        existingPrograms={allPrograms}
      />

      {/* 프로그램 수정 모달 */}
      <EditProgramModal
        isOpen={isEditProgramModalOpen}
        onClose={() => {
          setIsEditProgramModalOpen(false);
          setProgramToEdit(null);
        }}
        onSubmit={handleEditProgram}
        program={programToEdit}
      />

      {/* 관찰 항목 추가 모달 */}
      <AddObservationBehaviorModal
        isOpen={isAddObservationModalOpen}
        onClose={() => setIsAddObservationModalOpen(false)}
        onSubmit={handleAddObservation}
        studentId={selectedStudentId}
        userId={userProfile?.id}
      />

      {/* 관찰 항목 수정 모달 */}
      <EditObservationBehaviorModal
        isOpen={isEditObservationModalOpen}
        onClose={() => {
          setIsEditObservationModalOpen(false);
          setObservationToEdit(null);
        }}
        onSubmit={handleEditObservation}
        behavior={observationToEdit}
      />
    </Container>
  );
};

export default InfoPage;
